import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useAuth } from '@/core/AuthContext'
import { usePhoneAuth } from '../lib/usePhoneAuth'
import { usePhoneAuthDebug } from '../hooks/usePhoneAuthDebug'
import { ALERTS } from '../styles/classNames'

const DEBUG_PANEL_ENABLED = false
const INPUT_CLASS = 'w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent'
const CODE_INPUT_CLASS = `${INPUT_CLASS} text-center text-2xl tracking-widest`

export function ClientPhoneLoginPage() {
  const navigate = useNavigate()
  const { pendingTrainerInvites, pendingTrainerInvitesLoading } = useAuth()
  const { addDebugLog, clearDebugLog, debugLog, handleTestNative, isNative, nativePlatform } = usePhoneAuthDebug({ enabled: DEBUG_PANEL_ENABLED })

  const {
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    confirmation,
    loading,
    error,
    verified,
    sendCode,
    verifyCode,
    reset,
    user,
    userData,
    authLoading,
  } = usePhoneAuth({
    addDebugLog,
    onVerified: async (freshUserData) => {
      if (freshUserData) {
        addDebugLog(`Immediate redirect: role=${freshUserData.role}`)
        if (freshUserData.role === 'trainer') {
          navigate('/trainer', { replace: true })
        } else if (freshUserData.onboardingComplete) {
          navigate('/client', { replace: true })
        } else {
          navigate('/onboarding', { replace: true })
        }
      } else {
        addDebugLog('No userData after refresh, going to onboarding')
        navigate('/onboarding', { replace: true })
      }
    },
  })

  const isCodeStep = Boolean(confirmation)

  // Auto-redirect if already logged in
  useEffect(() => {
    if (authLoading || confirmation || !userData) return
    if (pendingTrainerInvitesLoading) {
      addDebugLog('[Redirect] Waiting for pending invite check')
      return
    }
    if (pendingTrainerInvites.length > 0) {
      addDebugLog(`[Redirect] Holding redirect for ${pendingTrainerInvites.length} pending invite(s)`)
      return
    }
    if (userData.role === 'trainer') {
      addDebugLog('[Redirect] Navigating to /trainer')
      navigate('/trainer', { replace: true })
      return
    }
    if (userData.role === 'client') {
      addDebugLog('[Redirect] Navigating to /client')
      navigate('/client', { replace: true })
    }
  }, [addDebugLog, authLoading, confirmation, navigate, pendingTrainerInvites, pendingTrainerInvitesLoading, userData])

  // Fallback redirect if verified but no userData after 5s
  useEffect(() => {
    if (!verified) return

    const timeout = setTimeout(() => {
      if (!(verified && user && !userData)) return

      void (async () => {
        addDebugLog('⚠️ Fallback redirect: verified but no userData after 5s')
        addDebugLog(`   user.uid: ${user.uid}`)

        try {
          const tokenResult = await user.getIdTokenResult()
          const isTrainer = tokenResult.claims.role === 'trainer' || Boolean(tokenResult.claims.trainerId)
          const destination = isTrainer ? '/trainer' : '/client'
          addDebugLog(`   fallback destination: ${destination}`)
          navigate(destination, { replace: true })
        } catch (err: any) {
          addDebugLog(`   fallback claims lookup failed: ${err?.message || err}`)
          navigate('/client', { replace: true })
        }
      })()
    }, 5000)

    return () => clearTimeout(timeout)
  }, [addDebugLog, navigate, user, userData, verified])

  return (
    <div className="min-h-screen flex items-center justify-center p-4 safe-all" data-testid="phone-login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <Card variant="elevated" padding="lg" className="shadow-2xl">
          <div className="text-center mb-8">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent">
              Start Grounded
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-gray-600 dark:text-slate-400 text-lg">{isCodeStep ? 'Enter verification code' : 'Sign in with your phone'}</motion.p>
          </div>

          {error && <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`mb-6 text-sm ${ALERTS.error}`}>{error}</motion.div>}

          {!isCodeStep ? (
            <motion.form onSubmit={sendCode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Phone Number</label>
                <input id="phone" data-testid="phone-login-phone-input" type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="(555) 123-4567" required className={INPUT_CLASS} />
              </div>
              <div id="recaptcha-container" className="recaptcha-container--hidden" />
              <Button type="submit" data-testid="phone-login-send-code-button" disabled={loading || !phoneNumber} isLoading={loading} variant="primary" size="lg" className="w-full">{loading ? 'Sending code...' : 'Send verification code'}</Button>
            </motion.form>
          ) : (
            <motion.form onSubmit={verifyCode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Verification Code</label>
                <input id="code" data-testid="phone-login-code-input" type="tel" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" required maxLength={6} className={CODE_INPUT_CLASS} />
                <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">Check your phone for a 6-digit code</p>
              </div>
              <Button type="submit" data-testid="phone-login-verify-code-button" disabled={loading || verificationCode.length !== 6} isLoading={loading} variant="primary" size="lg" className="w-full">{loading ? 'Verifying...' : 'Verify code'}</Button>
              <Button type="button" data-testid="phone-login-reset-button" onClick={reset} disabled={loading} variant="ghost" size="md" className="w-full">Use different number</Button>
            </motion.form>
          )}

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-6 text-center text-sm text-gray-600 dark:text-slate-400">
            <p>By continuing, you agree to receive phone calls for workout reminders.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10 text-center">
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Are you a trainer? <Link to="/trainer-auth" className="text-primary-600 dark:text-primary-400 hover:underline font-medium" data-testid="trainer-auth-link">Sign in here</Link>
            </p>
          </motion.div>
        </Card>

        {DEBUG_PANEL_ENABLED && (
          <div className="mt-4 bg-gray-900 text-green-400 font-mono text-xs p-3 rounded max-h-80 overflow-y-auto">
            <div className="flex justify-between mb-2 border-b border-gray-700 pb-2">
              <span className="font-bold">Debug Panel</span>
              <div className="flex gap-2">
                <button onClick={() => void handleTestNative()} className="text-cyan-400 hover:text-cyan-300">Test Plugin</button>
                <button onClick={clearDebugLog} className="text-red-400 hover:text-red-300">Clear</button>
              </div>
            </div>
            <div className="mb-2 text-yellow-400">Native: {isNative ? '✅ YES' : '❌ NO'} | Platform: {nativePlatform}</div>
            {debugLog.length === 0 ? (
              <p className="text-gray-500">No logs yet...</p>
            ) : (
              debugLog.map((log, index) => (
                <p
                  key={index}
                  className={
                    log.includes('❌')
                      ? 'text-red-400'
                      : log.includes('✅')
                        ? 'text-green-400'
                        : log.includes('===')
                          ? 'text-yellow-400 font-bold'
                          : log.includes('🔧')
                            ? 'text-cyan-400'
                            : log.includes('📱')
                              ? 'text-blue-400'
                              : log.includes('[Redirect]')
                                ? 'text-purple-400'
                                : 'text-gray-300'
                  }
                >
                  {log}
                </p>
              ))
            )}
          </div>
        )}
      </motion.div>
    </div>
  )
}
